import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { ArrowRight, ArrowUpRight, Loader2, Check, ChevronLeft, ChevronRight, Star, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SignupForm from "@/components/SignupForm";

const BG = "#F4F3EF";
const BG2 = "#EBEAE4";
const INK = "#1A1A18";
const INK2 = "#5C5C58";
const ACCENT = "#C25934";
const HERO_PORTRAIT = "https://images.unsplash.com/photo-1581182800629-7d90925ad072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwcG9ydHJhaXQlMjBuYXR1cmFsJTIwbGlnaHR8ZW58MHx8fHwxNzg1MTM2MzA3fDA&ixlib=rb-4.1.0&q=85";
const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
import { openCookieSettings } from "@/lib/consent";
const LEGAL_LINKS = [
  { slug: "terms", label: "Terms of Service" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "cookies", label: "Cookie Policy" },
  { slug: "refunds", label: "Returns & Refunds" },
  { slug: "disclaimer", label: "Disclaimer" },
  { slug: "imprint", label: "Contact & Imprint" },
];
const DEFAULT_DISCLAIMER = "StoreIG is an independent platform and is not affiliated with, endorsed by, sponsored by, or in any way officially connected to Instagram or Meta Platforms, Inc.";

export default function DirectoryV4({ content, stores, loading }) {
  const [signupOpen, setSignupOpen] = useState(false);
  const [subscribing, setSubscribing] = useState(null);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const handlePlan = async (p, i) => {
    const amount = parseFloat(String(p.price).replace(/[^\d.,]/g, "").replace(",", "."));
    if (content.subscriptions_enabled && amount > 0) {
      setSubscribing(i);
      try {
        const res = await api.post("/payments/subscribe", { plan_index: i, plan_id: p.id || "", origin_url: window.location.origin });
        window.location.href = res.data.checkout_url;
      } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setSubscribing(null); }
    } else { setSignupOpen(true); }
  };

  const categories = Array.from(new Set(stores.map((s) => s.category).filter(Boolean)));
  const catCounts = categories.map((c) => ({ name: c, count: stores.filter((s) => s.category === c).length }));
  const featured = stores.filter((s) => s.featured);
  const carousel = featured.length ? featured : stores.slice(0, 8);

  return (
    <div className="min-h-screen relative" style={{ background: BG, color: INK, fontFamily: "Manrope, ui-sans-serif, system-ui" }}>
      <div className="v4-grain pointer-events-none fixed inset-0 z-0" aria-hidden />

      {/* Nav */}
      <header className="sticky top-0 z-40 border-b" style={{ background: BG, borderColor: "rgba(26,26,24,0.12)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div style={serif} className="text-3xl tracking-tight leading-none" data-testid="directory-brand">StoreIG</div>
          <nav className="hidden lg:flex items-center gap-10 text-xs uppercase tracking-[0.18em]" style={{ color: INK2 }}>
            <button onClick={() => scrollTo("stores")} className="v4-link hover:text-[#1A1A18] transition-colors">Stores</button>
            <button onClick={() => scrollTo("categories")} className="v4-link hover:text-[#1A1A18] transition-colors">Categories</button>
            <a href="#pricing" className="v4-link hover:text-[#1A1A18] transition-colors">Membership</a>
            <button onClick={() => setSignupOpen(true)} className="v4-link hover:text-[#1A1A18] transition-colors">For Merchants</button>
          </nav>
          <div className="flex items-center gap-5">
            <Link to="/login" data-testid="admin-cta" className="hidden sm:block text-xs uppercase tracking-[0.18em] hover:text-[#C25934] transition-colors" style={{ color: INK2 }}>Log in</Link>
            <button onClick={() => setSignupOpen(true)} data-testid="signup-cta"
              className="rounded-full px-7 py-3 text-xs uppercase tracking-[0.16em] text-white active:scale-95 transition-transform" style={{ background: ACCENT }}>
              Become a merchant
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 border-b" style={{ borderColor: "rgba(26,26,24,0.12)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[1.15fr_0.85fr]">
          <div className="py-20 md:py-28 lg:pr-16 flex flex-col justify-center lg:border-r" style={{ borderColor: "rgba(26,26,24,0.12)" }}>
            <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="text-xs uppercase tracking-[0.35em] mb-8" style={{ color: ACCENT }} data-testid="hero-eyebrow">
              {content.hero_eyebrow}
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              style={serif} className="font-light leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl" data-testid="hero-title">
              {content.hero_title}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-8 max-w-md text-base md:text-lg font-light leading-relaxed" style={{ color: INK2 }} data-testid="hero-subtitle">
              {content.hero_subtitle}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-10 flex flex-wrap items-center gap-4">
              <button onClick={() => scrollTo("stores")} data-testid="hero-explore"
                className="group inline-flex items-center gap-2.5 rounded-full px-8 py-3.5 text-xs uppercase tracking-[0.16em] text-white active:scale-95 transition-transform" style={{ background: ACCENT }}>
                {content.cta_primary_label} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => setSignupOpen(true)}
                className="rounded-full px-8 py-3.5 text-xs uppercase tracking-[0.16em] border transition-colors hover:text-white"
                style={{ borderColor: INK, color: INK }}
                onMouseEnter={(e) => (e.currentTarget.style.background = INK)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                {content.cta_secondary_label}
              </button>
            </motion.div>

            {!loading && stores.length > 0 && (
              <div className="mt-14 flex items-center gap-5" data-testid="hero-reviews-badge">
                <div className="flex -space-x-3">
                  {stores.slice(0, 4).map((s) => (
                    <img key={s.id} src={imgUrl(s.logo_url)} alt="" className="h-10 w-10 rounded-full object-cover" style={{ boxShadow: `0 0 0 2px ${BG}` }} />
                  ))}
                </div>
                <div className="text-xs" style={{ color: INK2 }}>
                  <span className="inline-flex gap-0.5 align-middle mr-2">{[...Array(5)].map((_, i) => <Star key={i} size={12} style={{ color: ACCENT }} className="fill-current" />)}</span>
                  <span className="uppercase tracking-wide"><b style={{ color: INK }}>{stores.length}</b> curated houses · 4.9 avg</span>
                </div>
              </div>
            )}
          </div>

          <div className="relative min-h-[420px] lg:min-h-[640px] overflow-hidden">
            <motion.img initial={{ scale: 1.08, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1 }}
              src={content.hero_image ? imgUrl(content.hero_image) : HERO_PORTRAIT} alt="StoreIG editorial"
              className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute bottom-0 left-0 right-0 px-8 py-6" style={{ background: "rgba(244,243,239,0.9)" }}>
              <span className="text-[10px] uppercase tracking-[0.3em]" style={{ color: INK2 }}>The Instagram Concept Store — Est. 2026</span>
            </div>
          </div>
        </div>
      </section>

      {/* Shop by category */}
      {catCounts.length > 0 && (
        <section id="categories" className="relative z-10 border-b" style={{ borderColor: "rgba(26,26,24,0.12)" }}>
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: ACCENT }}>Browse the index</span>
            <div className="mt-8 flex flex-wrap items-baseline gap-x-10 gap-y-4" data-testid="category-row">
              {catCounts.map((c) => (
                <button key={c.name} onClick={() => scrollTo("stores")} data-testid={`category-card-${c.name}`}
                  style={serif} className="group inline-flex items-start gap-1.5 text-4xl md:text-5xl font-light tracking-tight leading-none hover:text-[#C25934] transition-colors">
                  {c.name}<sup className="text-xs font-sans" style={{ color: INK2 }}>{String(c.count).padStart(2, "0")}</sup>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured stores */}
      <FeaturedV4 heading={content.stores_heading} stores={carousel} loading={loading} onExplore={() => scrollTo("stores")} />

      {/* All stores */}
      <section id="stores" className="relative z-10 border-b" style={{ borderColor: "rgba(26,26,24,0.12)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-end justify-between mb-14">
            <h2 style={serif} className="text-5xl md:text-6xl font-light tracking-tight leading-none">The Directory</h2>
            <span className="text-xs uppercase tracking-[0.2em] pb-2" style={{ color: INK2 }}>{stores.length} houses</span>
          </div>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "rgba(26,26,24,0.12)" }}>
              {[...Array(6)].map((_, i) => <div key={i} className="h-80 animate-pulse" style={{ background: BG2 }} />)}
            </div>
          ) : stores.length === 0 ? (
            <p className="py-16 font-light" style={{ color: INK2 }} data-testid="stores-empty">No houses have opened yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px border" style={{ background: "rgba(26,26,24,0.12)", borderColor: "rgba(26,26,24,0.12)" }} data-testid="store-directory-grid">
              {stores.map((s) => <StoreCardV4 key={s.id} s={s} />)}
            </div>
          )}
        </div>
      </section>

      {/* Pricing */}
      {content.pricing?.length ? (
        <section id="pricing" className="relative z-10 border-b" style={{ borderColor: "rgba(26,26,24,0.12)" }} data-testid="pricing-section">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="mb-16 max-w-xl">
              <span className="text-xs uppercase tracking-[0.3em]" style={{ color: ACCENT }}>Membership</span>
              <h2 style={serif} className="mt-4 text-5xl md:text-6xl font-light tracking-tight leading-none">{content.pricing_heading}</h2>
            </div>
            <div className="grid md:grid-cols-3 border-t border-l" style={{ borderColor: "rgba(26,26,24,0.12)" }}>
              {content.pricing.map((p, i) => (
                <div key={p.id || `price-${i}`} data-testid={`pricing-box-${i}`}
                  className="p-8 md:p-10 border-r border-b flex flex-col" style={{ borderColor: "rgba(26,26,24,0.12)", background: p.highlight ? BG2 : "transparent" }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-[0.2em]" style={{ color: INK2 }}>{p.name}</span>
                    {p.highlight && <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full text-white" style={{ background: ACCENT }}>Preferred</span>}
                  </div>
                  <div className="mt-6 flex items-end gap-1.5">
                    <span style={serif} className="text-6xl font-light tracking-tight leading-none">{p.price}</span>
                    <span className="mb-1.5 text-sm font-light" style={{ color: INK2 }}>{p.period}</span>
                  </div>
                  <ul className="mt-8 space-y-3.5 flex-1">
                    {(p.features || []).map((f, fi) => (
                      <li key={fi} className="flex items-start gap-3 text-sm font-light" style={{ color: INK2 }}>
                        <Check size={15} className="mt-0.5 shrink-0" style={{ color: ACCENT }} /> {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => handlePlan(p, i)} disabled={subscribing === i} data-testid={`pricing-cta-${i}`}
                    className={`mt-10 rounded-full py-3.5 text-xs uppercase tracking-[0.16em] transition-colors active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60 ${p.highlight ? "text-white" : "border hover:text-white"}`}
                    style={p.highlight ? { background: ACCENT } : { borderColor: INK, color: INK }}
                    onMouseEnter={(e) => { if (!p.highlight) e.currentTarget.style.background = INK; }} onMouseLeave={(e) => { if (!p.highlight) e.currentTarget.style.background = "transparent"; }}>
                    {subscribing === i && <Loader2 size={14} className="animate-spin" />} {p.cta}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Reviews */}
      {content.reviews?.length ? <ReviewsV4 heading={content.reviews_heading} reviews={content.reviews} /> : null}

      {/* Footer */}
      <footer className="relative z-10" style={{ background: BG2, borderTop: "1px solid rgba(26,26,24,0.12)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <h2 style={serif} className="text-4xl md:text-6xl font-light tracking-tight leading-[1.05] max-w-2xl" data-testid="footer-tagline">
            {content.footer_tagline}
          </h2>
          <div className="mt-20 grid gap-12 md:grid-cols-4 border-t pt-12" style={{ borderColor: "rgba(26,26,24,0.12)" }}>
            <div>
              <div style={serif} className="text-2xl tracking-tight">StoreIG</div>
              <p className="mt-3 text-sm font-light max-w-xs" style={{ color: INK2 }}>The Instagram Concept Store.</p>
            </div>
            <FooterCol title="Explore">
              <li><button onClick={() => scrollTo("stores")} className="v4-link hover:text-[#C25934] transition-colors" data-testid="footer-stores">Stores</button></li>
              <li><button onClick={() => scrollTo("categories")} className="v4-link hover:text-[#C25934] transition-colors">Categories</button></li>
              <li><button onClick={() => setSignupOpen(true)} className="v4-link hover:text-[#C25934] transition-colors" data-testid="footer-signup">Become a merchant</button></li>
              <li><Link to="/login" className="v4-link hover:text-[#C25934] transition-colors" data-testid="footer-login">Merchant login</Link></li>
            </FooterCol>
            <FooterCol title="Legal">
              {LEGAL_LINKS.map((l) => (
                <li key={l.slug}><Link to={`/legal/${l.slug}`} data-testid={`footer-legal-${l.slug}`} className="v4-link hover:text-[#C25934] transition-colors">{l.label}</Link></li>
              ))}
              <li><button onClick={openCookieSettings} data-testid="footer-cookie-settings" className="v4-link hover:text-[#C25934] transition-colors">Cookie settings</button></li>
            </FooterCol>
            <FooterCol title="More from us">
              <li><a href="https://tiktokwebstore.com" target="_blank" rel="noreferrer" data-testid="footer-sister-tiktok" className="inline-flex items-center gap-1.5 hover:text-[#C25934] transition-colors">TikTokWebstore.com <ArrowUpRight size={12} /></a></li>
              <li><a href="https://wawebstore.com" target="_blank" rel="noreferrer" data-testid="footer-sister-wa" className="inline-flex items-center gap-1.5 hover:text-[#C25934] transition-colors">WAwebstore.com <ArrowUpRight size={12} /></a></li>
            </FooterCol>
          </div>
          <div className="mt-16 pt-8 border-t" style={{ borderColor: "rgba(26,26,24,0.12)" }} data-testid="footer-affiliation-note">
            <div className="rounded-xl border p-5 mb-8" style={{ borderColor: "rgba(26,26,24,0.28)", background: "rgba(26,26,24,0.035)" }}>
              <div className="flex items-center gap-2 mb-1.5" style={{ color: INK }}>
                <Info size={16} strokeWidth={2} />
                <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Independent platform</span>
              </div>
              <p className="text-sm leading-relaxed max-w-3xl font-medium" style={{ color: INK }} data-testid="footer-disclaimer">{content.disclaimer || DEFAULT_DISCLAIMER}</p>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <span className="text-xs" style={{ color: INK2 }}>© {new Date().getFullYear()} StoreIG. All rights reserved.</span>
              <a href={content.brand_url || "#"} target="_blank" rel="noreferrer" aria-label={content.brand_name || "Brand"} className="flex items-center gap-3" data-testid="footer-brand">
                <span className="text-[10px] uppercase tracking-[0.3em]" style={{ color: INK2 }}>A brand by</span>
                {content.brand_logo_url
                  ? <img src={imgUrl(content.brand_logo_url)} alt={content.brand_name || "Brand"} data-testid="footer-brand-logo" className="h-10 w-auto opacity-80 hover:opacity-100 transition-opacity" />
                  : <span data-testid="footer-brand-name" style={serif} className="text-xl tracking-tight">{content.brand_name}</span>}
              </a>
            </div>
          </div>
        </div>
      </footer>

      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="signup-dialog">
          <DialogHeader><DialogTitle style={serif} className="text-3xl font-light tracking-tight">Join StoreIG</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm -mt-1" style={{ color: INK2 }}>Tell us about your house and we'll help you open your doors.</DialogDescription>
          <SignupForm onSuccess={() => setSignupOpen(false)} />
        </DialogContent>
      </Dialog>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400&family=Manrope:wght@300;400;500;600&display=swap');
        .v4-grain{background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E");opacity:0.04;}
        .v4-link{position:relative;}
        .v4-link::after{content:"";position:absolute;left:0;bottom:-3px;height:1px;width:0;background:#C25934;transition:width .3s ease;}
        .v4-link:hover::after{width:100%;}
      `}</style>
    </div>
  );
}

const FooterCol = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.25em] mb-5" style={{ color: "#9a9a94" }}>{title}</div>
    <ul className="space-y-3 text-sm font-light" style={{ color: INK2 }}>{children}</ul>
  </div>
);

const StoreCardV4 = ({ s }) => (
  <Link to={`/${s.handle}`} data-testid={`store-card-${s.handle}`} className="group block" style={{ background: BG }}>
    <div className="relative aspect-[4/3] overflow-hidden" style={{ background: BG2 }}>
      {s.banner_url ? <img src={imgUrl(s.banner_url)} alt="" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" /> : null}
      {s.featured && (
        <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full text-white" style={{ background: ACCENT }} data-testid={`featured-badge-${s.handle}`}>Featured</span>
      )}
    </div>
    <div className="p-7 border-t flex gap-4" style={{ borderColor: "rgba(26,26,24,0.12)" }}>
      <img src={imgUrl(s.logo_url)} alt={s.name} className="h-12 w-12 rounded-full object-cover shrink-0" style={{ background: BG2 }} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <h3 style={serif} className="text-2xl font-light tracking-tight leading-none truncate">{s.name}</h3>
          <ArrowUpRight size={17} style={{ color: INK2 }} className="group-hover:text-[#C25934] transition-colors shrink-0" />
        </div>
        <p className="italic text-base mt-1.5 leading-snug line-clamp-2" style={{ color: INK2, fontFamily: "'Cormorant Garamond', serif" }}>{s.tagline}</p>
        <div className="mt-4 flex items-center gap-4 text-[10px] uppercase tracking-[0.18em]" style={{ color: INK2 }}>
          {s.category ? <span>{s.category}</span> : null}
          <span>·</span>
          <span>{s.product_count} pieces</span>
        </div>
      </div>
    </div>
  </Link>
);

const FeaturedV4 = ({ heading, stores, loading, onExplore }) => {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * 440, behavior: "smooth" });
  if (loading || !stores.length) return null;
  return (
    <section className="relative z-10 border-b" style={{ borderColor: "rgba(26,26,24,0.12)", background: BG2 }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: ACCENT }}>In the spotlight</span>
            <h2 style={serif} className="mt-4 text-5xl md:text-6xl font-light tracking-tight leading-none">{heading}</h2>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onExplore} data-testid="featured-view-all" className="v4-link hidden sm:inline text-xs uppercase tracking-[0.18em] pb-2" style={{ color: INK2 }}>View all</button>
            <button onClick={() => scroll(-1)} aria-label="Previous" data-testid="featured-prev" className="h-11 w-11 rounded-full border flex items-center justify-center transition-colors hover:text-white" style={{ borderColor: INK }} onMouseEnter={(e) => (e.currentTarget.style.background = INK)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}><ChevronLeft size={18} /></button>
            <button onClick={() => scroll(1)} aria-label="Next" data-testid="featured-next" className="h-11 w-11 rounded-full border flex items-center justify-center transition-colors hover:text-white" style={{ borderColor: INK }} onMouseEnter={(e) => (e.currentTarget.style.background = INK)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}><ChevronRight size={18} /></button>
          </div>
        </div>
        <div ref={ref} className="flex gap-6 overflow-x-auto pb-2 v4-scroll" data-testid="featured-carousel">
          {stores.map((s) => (
            <Link to={`/${s.handle}`} key={s.id} data-testid={`featured-follow-${s.handle}`}
              className="group shrink-0 w-[380px] relative overflow-hidden border" style={{ borderColor: "rgba(26,26,24,0.12)", background: BG }}>
              <div className="aspect-[4/5] overflow-hidden" style={{ background: BG2 }}>
                {s.banner_url ? <img src={imgUrl(s.banner_url)} alt="" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" /> : <img src={imgUrl(s.logo_url)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center gap-4" style={{ background: "rgba(244,243,239,0.94)" }}>
                <img src={imgUrl(s.logo_url)} alt={s.name} className="h-12 w-12 rounded-full object-cover shrink-0" style={{ background: BG2 }} />
                <div className="min-w-0 flex-1">
                  <div style={serif} className="text-2xl font-light tracking-tight leading-none truncate">{s.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] mt-1.5" style={{ color: INK2 }}>{s.category || `@${s.handle}`}</div>
                </div>
                <span className="text-xs uppercase tracking-[0.16em] inline-flex items-center gap-1 group-hover:text-[#C25934] transition-colors">Visit <ArrowRight size={13} /></span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const ReviewsV4 = ({ heading, reviews }) => {
  const [idx, setIdx] = useState(0);
  const go = (d) => setIdx((i) => (i + d + reviews.length) % reviews.length);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 6000);
    return () => clearInterval(t);
  }, [reviews.length]);
  const r = reviews[idx];
  return (
    <section className="relative z-10 border-b" style={{ borderColor: "rgba(26,26,24,0.12)" }} data-testid="reviews-section">
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <span className="text-xs uppercase tracking-[0.3em]" style={{ color: ACCENT }}>{heading}</span>
        <div className="relative mt-10 min-h-[240px]">
          <span style={serif} className="absolute -top-10 -left-2 text-[9rem] leading-none font-light select-none" aria-hidden style2="" >{'"'}</span>
          <AnimatePresence mode="wait">
            <motion.div key={idx} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.5 }} data-testid="review-slide">
              <p style={serif} className="italic font-light text-3xl md:text-4xl leading-snug tracking-tight" style2="">{r.text}</p>
              <div className="mt-8 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm" style={{ background: ACCENT }}>{(r.name || "?").charAt(0)}</div>
                <div>
                  <div className="text-sm font-medium">{r.name}</div>
                  <div className="text-xs" style={{ color: INK2 }}>{r.handle}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="mt-12 flex items-center gap-5">
          <button onClick={() => go(-1)} data-testid="review-prev" className="h-11 w-11 rounded-full border flex items-center justify-center transition-colors hover:text-white" style={{ borderColor: INK }} onMouseEnter={(e) => (e.currentTarget.style.background = INK)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}><ChevronLeft size={17} /></button>
          <button onClick={() => go(1)} data-testid="review-next" className="h-11 w-11 rounded-full border flex items-center justify-center transition-colors hover:text-white" style={{ borderColor: INK }} onMouseEnter={(e) => (e.currentTarget.style.background = INK)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}><ChevronRight size={17} /></button>
          <div className="flex gap-2 ml-2">
            {reviews.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} data-testid={`review-dot-${i}`} className="h-1.5 rounded-full transition-all" style={{ width: i === idx ? 28 : 8, background: i === idx ? ACCENT : "rgba(26,26,24,0.2)" }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
